export interface ContentItem {
  slug: string;
  category: 'kyber' | 'ai' | 'mobile' | 'crisis' | 'privacy' | 'routines' | 'cert';
  featured: boolean;
  navLabel: string;
  version: string;
  visibility: 'public' | 'homepage' | 'admin_only';
  seo: { title: string; description: string };
  hero: { title: string; subtitle: string };
  sections: Array<{ id: string, title: string; body: string }>;
  checklist: string[];
  cta: { text: string; route: string | null };
  quizRef?: string;
  qrCode?: {
    value: string;
    label: string;
    description: string;
  };
  quickActions?: {
    today: string[];
    week: string[];
    month: string[];
  };
  pitfalls?: string[];
}

export const contentRegistry: Record<string, ContentItem> = {
  "kyber-perusteet": {
    "slug": "kyber-perusteet",
    "category": "kyber",
    "featured": true,
    "version": "1.5.0",
    "visibility": "public",
    "navLabel": "Kyberperusteet",
    "seo": { "title": "Kyberturvallisuuden perusteet | Lapland AI Lab", "description": "CIA-malli, MFA ja salasanojen hallinta." },
    "hero": { "title": "Kyberturvallisuuden peruspilarit", "subtitle": "Luottamuksellisuus, Eheys ja Saatavuus. Digitaalisen turvan kivijalka." },
    "sections": [
      { "id": "cia-malli", "title": "CIA-malli: Tietoturvan perusmalli", "body": "CIA-malli (Confidentiality, Integrity, Availability) on kansainvälinen standardi tietoturvan suunnitteluun. Se varmistaa, että tieto pysyy salassa, oikeana ja käytettävissä." },
      { "id": "tiedon-luokittelu", "title": "Tiedon luokittelu", "body": "Tietojen luokittelu auttaa hahmottamaan, missä suurin riski piilee. Luokittele tieto julkiseksi, sisäiseksi, luottamukselliseksi tai salaiseksi." },
      { "id": "mfa", "title": "Salasanojen hallinta ja MFA", "body": "Valtaosa murroista alkaa heikoista tunnuksista. MFA (Monivaiheinen tunnistautuminen) on tehokkain yksittäinen suoja hyökkäyksiä vastaan." }
    ],
    "checklist": ["CIA-malli sisäistetty", "Tieto luokiteltu kriittisyyden mukaan", "MFA käytössä kaikissa tärkeissä tileissä", "Salasanaohjelma asennettu"],
    "cta": { "text": "Testaa osaaminen", "route": "/quiz/kyber-perusteet" },
    "quizRef": "kyber-perusteet"
  },
  "ai-safety": {
    "slug": "ai-safety",
    "category": "ai",
    "featured": true,
    "version": "1.5.0",
    "visibility": "public",
    "navLabel": "AI-turvallisuus",
    "seo": { "title": "AI-turvallisuus ja uudet uhat | Lapland AI Lab", "description": "Prompt-injektiot, deepfaket ja Shadow AI." },
    "hero": { "title": "Tekoäly ja uudet uhat", "subtitle": "Tekoäly muuttaa työtä, mutta tuo mukanaan uudenlaisia hyökkäysvektoreita." },
    "sections": [
      { "id": "prompt-injektio", "title": "Prompt-injektio: Mallin puijaaminen", "body": "Prompt-injektio on hyökkäys, jossa kielimallille syötetään tekstiä, joka kumoaa sen alkuperäiset ohjeet." },
      { "id": "deepfake", "title": "Deepfake-huijaukset", "body": "Tekoäly pystyy jäljittelemään ääntä ja kuvaa pelottavan aidosti. 'Toimitusjohtajahuijaukset' siirtyvät videopuheluihin." },
      { "id": "shadow-ai", "title": "Hallitsematon tekoäly (Shadow AI)", "body": "Työntekijöiden oma-aloitteinen tekoälyn käyttö ilman valvontaa altistaa yrityksen tietovuodoille." }
    ],
    "checklist": ["Prompt-injektiot huomioitu kehityksessä", "Turvasana sovittu kriittisiin toimiin", "Shadow AI -ohjeistus jaettu", "Henkilötietoja ei syötetä julkisiin malleihin"],
    "cta": { "text": "Suorita AI-testi", "route": "/quiz/ai-turva" },
    "quizRef": "ai-turva"
  },
  "mobiiliturva": {
    "slug": "mobiiliturva",
    "category": "mobile",
    "featured": true,
    "version": "1.5.0",
    "visibility": "public",
    "navLabel": "Mobiiliturva",
    "seo": { "title": "Mobiiliturvallisuus | Lapland AI Lab", "description": "Suojaa älypuhelimesi ja viestintäsi." },
    "hero": { "title": "Tasku-kyber", "subtitle": "Puhelimesi on todennäköisesti henkilökohtaisin ja samalla haavoittuvin laitteesi." },
    "sections": [
      { "id": "fyysinen-paasy", "title": "Fyysinen pääsy", "body": "Kadonnut puhelin on avoin ovi kaikkeen dataasi. Käytä vahvaa biometriaa ja PIN-koodia." },
      { "id": "juice-jacking", "title": "USB-lataus ja Juice Jacking", "body": "Julkiset USB-latauspisteet voivat siirtää dataa sähkön ohella. Käytä data blockeria." }
    ],
    "qrCode": {
      "value": "https://laplandailab.fi/tietoturva/vpn-config-setup",
      "label": "Mobiili-VPN Konfiguraatio",
      "description": "Skannaa tämä koodi mobiililaitteellasi ladataksesi suojatun VPN-profiilin esiasetukset."
    },
    "checklist": ["PIN-koodi (väh. 6 numeroa) käytössä", "Etätyhjennys aktivoitu", "USB Data Blocker hankittu", "VPN käytössä julkisissa verkoissa"],
    "cta": { "text": "Testaa mobiiliosaaminen", "route": "/quiz/mobiiliturva" },
    "quizRef": "mobiiliturva"
  },
  "tietosuoja-perusteet": {
    "slug": "tietosuoja-perusteet",
    "category": "privacy",
    "featured": true,
    "version": "1.5.0",
    "visibility": "public",
    "navLabel": "Tietosuoja",
    "seo": { "title": "Tietosuojan perusteet ja velvoitteet | Lapland AI Lab", "description": "Johdanto tietosuojaan, yksilön oikeuksiin ja Privacy by Design -periaatteisiin." },
    "hero": { "title": "Tietosuoja ja PbD", "subtitle": "Tietosuoja on perusoikeus, joka vaatii sekä teknistä suojausta että organisatorisia rutiineja." },
    "sections": [
      { "id": "p1", "title": "Johdanto tietosuojaan", "body": "Tietosuoja ei ole vain juridinen velvoite, vaan keskeinen osa luottamusta." },
      { "id": "p2", "title": "Privacy by Design (PbD)", "body": "PbD tarkoittaa, että tietosuoja huomioidaan projektin alusta lähtien." }
    ],
    "checklist": ["Tietojen minimointi toteutettu", "Käyttötarkoituksen rajaus määritelty"],
    "cta": { "text": "Suorita tietosuojatesti", "route": "/quiz/tietosuoja" },
    "quizRef": "tietosuoja"
  },
  "salaus-opas": {
    "slug": "salaus-opas",
    "category": "kyber",
    "featured": true,
    "version": "1.5.0",
    "visibility": "public",
    "navLabel": "Salaus",
    "seo": { "title": "Salausoppaan perusteet | Lapland AI Lab", "description": "AES, RSA ja avainten hallinta." },
    "hero": { "title": "Salaus: Datan viimeinen suojamuuri", "subtitle": "Salaus muuntaa luottamuksellisen tiedon lukukelvottomaan muotoon." },
    "sections": [
      { "id": "e1", "title": "Symmetrinen vs. Asymmetrinen", "body": "Symmetrisessä salauksessa käytetään samaa avainta. Asymmetrisessä julkista ja yksityistä." }
    ],
    "checklist": ["AES-256 käytössä", "Avainten hallinta määritelty"],
    "cta": { "text": "Suorita salaustesti", "route": "/quiz/salaus" },
    "quizRef": "salaus"
  },
  "routines": {
    "slug": "routines",
    "category": "routines",
    "featured": true,
    "version": "1.5.0",
    "visibility": "public",
    "navLabel": "Arjen rutiinit",
    "seo": { "title": "Tietoturvan tarkistuslistat | Lapland AI Lab", "description": "Viikoittaiset ja kuukausittaiset rutiinit." },
    "hero": { "title": "Turvallisuuden arjen rutiinit", "subtitle": "Tietoturva ei ole projekti, se on jatkuva tapa." },
    "sections": [
      { "id": "viikko", "title": "Maanantai-check (Viikoittainen)", "body": "Tarkista päivitykset ja varmuuskopiot viikoittain." },
      { "id": "kuukausi", "title": "Kuukauden huolto (Kuukausittainen)", "body": "Poista turhat sovellukset kerran kuussa." }
    ],
    "checklist": ["Päivitykset tarkistettu", "Varmuuskopiot validoitu"],
    "cta": { "text": "Suorita rutiinitesti", "route": "/quiz/routines" },
    "quizRef": "routines"
  },
  "master-certification": {
    "slug": "master-certification",
    "category": "cert",
    "featured": false,
    "version": "1.5.0",
    "visibility": "admin_only",
    "navLabel": "Master Certification",
    "seo": { "title": "Master Cert Validointi", "description": "Admin-only virtuaalimoduuli loppukokeelle." },
    "hero": { "title": "Arctic Master Certification", "subtitle": "Kaikki moduulit kattava loppukoe." },
    "sections": [
      { "id": "m1", "title": "Sertifioinnin kattavuus", "body": "Tämä osio kattaa kaikki aiemmat moduulit: Kyber, AI, Mobiili, Tietosuoja ja Rutiinit." }
    ],
    "checklist": ["Kaikki moduulit suoritettu"],
    "cta": { "text": "Aloita loppukoe", "route": "/quiz/sertifikaatti" },
    "quizRef": "sertifikaatti"
  },
  "incident-response": {
    "slug": "incident-response",
    "category": "crisis",
    "featured": false,
    "version": "1.5.0",
    "visibility": "public",
    "navLabel": "Kriisiprotokolla",
    "seo": { "title": "Incident Response -protokolla | Lapland AI Lab", "description": "0-60 minuutin toimintaohje tietomurron sattuessa." },
    "hero": { "title": "Kriisin 0-60 minuuttia", "subtitle": "Paniikki on vihollinen. Seuraa protokollaa, eristä ja minimoi vahingot." },
    "sections": [
      { "id": "cr1", "title": "Välitön reagointi", "body": "Ensimmäiset 15 minuuttia määrittävät vahingon suuruuden. Eristä saastuneet laitteet verkosta välittömästi." }
    ],
    "checklist": ["Eristä laite verkosta", "Ilmoita johdolle", "Dokumentoi tapahtumat"],
    "cta": { "text": "Kokeile protokollaa", "route": "/crisis-protocol" }
  },
  "trendit-2026": {
    "slug": "trendit-2026",
    "category": "ai",
    "featured": true,
    "version": "1.5.0",
    "visibility": "public",
    "navLabel": "Trendit 2026",
    "seo": { "title": "Kyberturvallisuuden trendit 2026 | Lapland AI Lab", "description": "Nollaluottamus, kvanttivalmius ja resilienssi." },
    "hero": { "title": "Tulevaisuuden kyber-resilienssi", "subtitle": "Resilienssi ei ole vain hyökkäysten estoa, vaan kykyä jatkaa toimintaa ja palautua nopeasti." },
    "sections": [
      { "id": "tr1", "title": "Nollaluottamus (Zero Trust)", "body": "Jokainen pyyntö vahvistetaan. Luottamusta ei ole, vaikka laite olisi sisäverkossa." }
    ],
    "checklist": ["Pääsynhallinta auditoitu", "Kvanttiuhka tunnistettu"],
    "cta": { "text": "Palaa etusivulle", "route": "/" }
  }
};